// src/pages/CompanySettingsPage.js
import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import companyService from '../services/companyService';
import SearchableSelect from '../components/SearchableSelect';
import bolService from '../services/bolService';
import labelService from '../services/labelService';
import qboService from '../services/qboService';
import xeroService from '../services/xeroService';
import r365Service from '../services/r365Service';
import deliveryFleetService from '../services/deliveryFleetService';
import sqfService from '../services/sqfService';
import ConfirmModal from '../components/ConfirmModal';
import billingService from '../services/billingService';
import { jwtDecode } from 'jwt-decode';
import { usePermissions } from '../context/PermissionContext';
import { generateLotCode, LOT_CODE_TOKENS, DEFAULT_LOT_CODE_FORMAT, DEFAULT_LOT_CODE_KEY } from '../utils/lotCodeUtils';
import axios from 'axios';

const DEFAULT_SHIP_VIA = [
  'LTL (Less Than Truckload)', 'FTL (Full Truckload)', 'Partial Truckload',
  'Refrigerated Truck', 'Courier / Parcel (FedEx / UPS / USPS)',
  'Air Freight', 'Ocean Freight', 'Rail', 'Flatbed',
  'Company Vehicle', 'Will Call / Customer Pickup', 'Other',
];

const DEFAULT_REFRIG_CHECKS = [
  { id: 'req_temp',     label: 'Required Temperature',      type: 'text',           defaultValue: '0°F (-18°C) or below', enabled: true },
  { id: 'trailer_odor',label: 'Trailer Clean & Odor Free',  type: 'yes_no',                                               enabled: true },
  { id: 'trailer_temp',label: 'Trailer Temperature',        type: 'temperature',    unit: 'F',                            enabled: true },
  { id: 'product_temp',label: 'Product Temperature',        type: 'temperature',    unit: 'F',                            enabled: true },
  { id: 'total_pallets',label:'Total Pallets Loaded',       type: 'number',         autoFill: 'palletCount',              enabled: true },
  { id: 'inspected_by',label: 'Inspected By',               type: 'text_input',                                           enabled: true },
  { id: 'signature',   label: 'Signature',                  type: 'signature_line',                                       enabled: true },
];

function getRoleFromToken() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try { return jwtDecode(token).role; } catch { return null; }
}

function CompanySettingsPage() {
  const { companySettings, refresh: refreshPermissions } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSuccess, setNameSuccess] = useState(false);

  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');

  const [navbarLogoUploading, setNavbarLogoUploading] = useState(false);
  const [navbarLogoError, setNavbarLogoError] = useState('');

  const [requireIngredientQty, setRequireIngredientQty] = useState(null);
  const [toggleSaving, setToggleSaving] = useState(false);

  const [deviationThreshold, setDeviationThreshold] = useState('');
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [thresholdSuccess, setThresholdSuccess] = useState(false);

  const [contact, setContact] = useState({ addressLine1: '', addressLine2: '', city: '', stateProvince: '', postalCode: '', country: '', phone: '', email: '' });
  const [contactSaving, setContactSaving] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  const [lotCodeFormat, setLotCodeFormat] = useState('');
  const [lotCodeFormatSaving, setLotCodeFormatSaving] = useState(false);
  const [lotCodeFormatSuccess, setLotCodeFormatSuccess] = useState(false);

  const [lotCodeKey, setLotCodeKey] = useState('');
  const [lotCodeKeySaving, setLotCodeKeySaving] = useState(false);
  const [lotCodeKeySuccess, setLotCodeKeySuccess] = useState(false);

  // BOL settings
  const [bolGeneral, setBolGeneral] = useState({ bolNumberPrefix: 'BOL', bolPalletIdFormat: '{N}', bolDefaultWeightUnit: 'lb', bolTitle: '', bolSubtitle: '', palletWeightKg: '18.14' });
  const [bolGeneralSaving, setBolGeneralSaving] = useState(false);
  const [bolGeneralSuccess, setBolGeneralSuccess] = useState(false);
  const [bolShowDisclaimer, setBolShowDisclaimer] = useState(true);
  const [bolDisclaimerText, setBolDisclaimerText] = useState('');
  const [bolDisclaimerSaving, setBolDisclaimerSaving] = useState(false);
  const [bolDisclaimerSuccess, setBolDisclaimerSuccess] = useState(false);
  const [bolShowRefrig, setBolShowRefrig] = useState(true);
  const [bolRefrigChecks, setBolRefrigChecks] = useState(DEFAULT_REFRIG_CHECKS);
  const [bolRefrigSaving, setBolRefrigSaving] = useState(false);
  const [bolRefrigSuccess, setBolRefrigSuccess] = useState(false);
  const [bolShipVia, setBolShipVia] = useState(DEFAULT_SHIP_VIA);
  const [bolShipViaSaving, setBolShipViaSaving] = useState(false);
  const [bolShipViaSuccess, setBolShipViaSuccess] = useState(false);
  const [newShipVia, setNewShipVia] = useState('');
  const bolSettingsInitialised = useRef(false);

  // Label settings
  const [labelTemplates, setLabelTemplates] = useState([]);
  const [labelTemplatesLoading, setLabelTemplatesLoading] = useState(false);
  const [showLabelTemplateForm, setShowLabelTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [labelTemplateSaving, setLabelTemplateSaving] = useState(false);
  const [labelTemplateError, setLabelTemplateError] = useState('');
  const BLANK_TEMPLATE = {
    name: '', isDefault: false, storageBanner: '', storageBannerColor: 'black',
    dateFormat: 'MM/DD/YYYY', dateLabel: 'Expiration', casesLabel: 'CASES ON PALLET',
    showLogo: true, showManufacturedBy: true, customFooterNote: '', labelSize: '4x6',
  };
  const [templateForm, setTemplateForm] = useState(BLANK_TEMPLATE);

  // Pay period settings
  const [payPeriodType, setPayPeriodType] = useState('semi_monthly');
  const [payPeriodSaving, setPayPeriodSaving] = useState(false);
  const [payPeriodSuccess, setPayPeriodSuccess] = useState(false);
  const [usdaEstablishmentNumber, setUsdaEstablishmentNumber] = useState('');
  const [usdaEstNumberSaving, setUsdaEstNumberSaving] = useState(false);
  const [usdaEstNumberSuccess, setUsdaEstNumberSuccess] = useState(false);
  const [requireFatSubProfile, setRequireFatSubProfile] = useState(true);
  const [fatSubProfileSaving, setFatSubProfileSaving] = useState(false);
  const [fatSubProfileSuccess, setFatSubProfileSuccess] = useState(false);

  // AI usage
  const [aiUsage, setAiUsage] = useState(null);

  // Accounting integration
  const [qboStatus, setQboStatus] = useState(null);
  const [qboLoading, setQboLoading] = useState(false);
  const [qboConnecting, setQboConnecting] = useState(false);
  const [qboDisconnecting, setQboDisconnecting] = useState(false);
  const [qboError, setQboError] = useState('');
  const [confirmQboDisconnect, setConfirmQboDisconnect] = useState(false);

  const [xeroStatus, setXeroStatus] = useState(null);
  const [xeroLoading, setXeroLoading] = useState(false);
  const [xeroConnecting, setXeroConnecting] = useState(false);
  const [xeroDisconnecting, setXeroDisconnecting] = useState(false);
  const [xeroError, setXeroError] = useState('');
  const [confirmXeroDisconnect, setConfirmXeroDisconnect] = useState(false);
  const [xeroOrgPickerTenants, setXeroOrgPickerTenants] = useState(null); // [{tenantId, name}] when multi-org
  const [xeroOrgPicking, setXeroOrgPicking] = useState(false);

  // QBO expense-account defaults (docs/RECEIPT_SCAN_MODULE.md's Phase 2) — the bank/credit-card
  // account a pushed receipt-scan expense posts against, and the fallback expense category for
  // ingredients that don't have their own (set per-ingredient in Ingredients Hub).
  const [qboExpenseAccountsList, setQboExpenseAccountsList] = useState([]);
  const [qboBankAccountsList, setQboBankAccountsList] = useState([]);
  const [qboPurchaseAccountId, setQboPurchaseAccountId] = useState('');
  const [qboDefaultExpenseAccountId, setQboDefaultExpenseAccountId] = useState('');
  const [qboSalesTaxAccountId, setQboSalesTaxAccountId] = useState('');
  const [qboAccountsSaving, setQboAccountsSaving] = useState(false);
  const [qboAccountsSuccess, setQboAccountsSuccess] = useState(false);

  // Restaurant 365 integration (outbound expense push only — no OAuth, Classic API Connector is
  // a plain username/password login; see docs/RECEIPT_SCAN_MODULE.md's R365 section).
  const [r365, setR365] = useState(null); // { connected, r365Subdomain, r365Username, hasPassword, r365DefaultLocation, r365DefaultGLAccount, r365DefaultGLAccountName }
  const [r365Form, setR365Form] = useState({ r365Subdomain: '', r365Username: '', r365Password: '', r365DefaultLocation: '', r365DefaultGLAccount: '' });
  const [r365Loading, setR365Loading] = useState(true);
  const [r365Saving, setR365Saving] = useState(false);
  const [r365Error, setR365Error] = useState('');
  const [r365Saved, setR365Saved] = useState(false);
  const [r365Testing, setR365Testing] = useState(false);
  const [r365TestResult, setR365TestResult] = useState(null);

  // Shopify integration (delivery fleet — manual webhook setup, no OAuth)
  const [shopify, setShopify] = useState(null); // { shopifyShopDomain, webhookSecret, defaultDriverPayoutUsd, webhookUrl }
  const [shopifyForm, setShopifyForm] = useState({ shopifyShopDomain: '', defaultDriverPayoutUsd: '' });
  const [shopifyLoading, setShopifyLoading] = useState(true);
  const [shopifySaving, setShopifySaving] = useState(false);
  const [shopifyError, setShopifyError] = useState('');
  const [shopifySaved, setShopifySaved] = useState(false);
  const [shopifyCopied, setShopifyCopied] = useState('');
  const [shopifyTesting, setShopifyTesting] = useState(false);
  const [shopifyTestResult, setShopifyTestResult] = useState(null);

  const fileInputRef = useRef();
  const navbarFileInputRef = useRef();

  // Initialise local state from context once loaded
  const initialised = useRef(false);
  if (companySettings && !initialised.current) {
    initialised.current = true;
    setCompanyName(companySettings.name || '');
    setRequireIngredientQty(companySettings.requireIngredientQty ?? true);
    setDeviationThreshold(String(companySettings.ingredientDeviationThreshold ?? 5));
    setLotCodeFormat(companySettings.lotCodeFormat || DEFAULT_LOT_CODE_FORMAT);
    setLotCodeKey(companySettings.lotCodeKey || DEFAULT_LOT_CODE_KEY);
    setPayPeriodType(companySettings.payPeriodType || 'semi_monthly');
    setUsdaEstablishmentNumber(companySettings.usdaEstablishmentNumber || '');
    setRequireFatSubProfile(companySettings.requireFatSubProfile !== false);
    setQboPurchaseAccountId(companySettings.qbPurchaseAccountId || '');
    setQboDefaultExpenseAccountId(companySettings.qbDefaultExpenseAccountId || '');
    setQboSalesTaxAccountId(companySettings.qbSalesTaxAccountId || '');

    // BOL settings — loaded separately on mount via bolService
    setContact({
      addressLine1:  companySettings.addressLine1  || '',
      addressLine2:  companySettings.addressLine2  || '',
      city:          companySettings.city          || '',
      stateProvince: companySettings.stateProvince || '',
      postalCode:    companySettings.postalCode    || '',
      country:       companySettings.country       || '',
      phone:         companySettings.phone         || '',
      email:         companySettings.email         || '',
    });
  }

  // Load BOL settings once on mount
  useEffect(() => {
    if (bolSettingsInitialised.current) return;
    bolSettingsInitialised.current = true;
    bolService.getSettings().then(s => {
      setBolGeneral({ bolNumberPrefix: s.bolNumberPrefix || 'BOL', bolPalletIdFormat: s.bolPalletIdFormat || '{N}', bolDefaultWeightUnit: s.bolDefaultWeightUnit || 'lb', bolTitle: s.bolTitle || '', bolSubtitle: s.bolSubtitle || '', palletWeightKg: s.palletWeightKg != null ? String(s.palletWeightKg) : '18.14' });
      setBolShowDisclaimer(s.bolShowDisclaimer !== false);
      setBolDisclaimerText(s.bolDisclaimerText || '');
      setBolShowRefrig(s.bolShowRefrigerationSection !== false);
      setBolRefrigChecks(Array.isArray(s.bolRefrigerationChecks) && s.bolRefrigerationChecks.length ? s.bolRefrigerationChecks : DEFAULT_REFRIG_CHECKS);
      setBolShipVia(Array.isArray(s.bolShipViaOptions) && s.bolShipViaOptions.length ? s.bolShipViaOptions : DEFAULT_SHIP_VIA);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLabelTemplatesLoading(true);
    labelService.getTemplates().then(setLabelTemplates).catch(() => {}).finally(() => setLabelTemplatesLoading(false));
  }, []);

  useEffect(() => {
    sqfService.getAiUsage().then(setAiUsage).catch(() => {});
  }, []);

  useEffect(() => {
    const role = getRoleFromToken();
    if (role === 'superadmin') return;
    billingService.getSubscriptionStatus().then(setSubscriptionInfo).catch(() => {});
  }, []);

  // Accounting: load both statuses on mount + handle OAuth callback params
  useEffect(() => {
    setQboLoading(true);
    qboService.getStatus().then(setQboStatus).catch(() => setQboStatus({ connected: false })).finally(() => setQboLoading(false));
    setXeroLoading(true);
    xeroService.getStatus().then(setXeroStatus).catch(() => setXeroStatus({ connected: false })).finally(() => setXeroLoading(false));
  }, []);

  // Account lists for the expense-category pickers (docs/RECEIPT_SCAN_MODULE.md's Phase 2) —
  // only worth fetching once QBO is actually connected.
  useEffect(() => {
    if (!qboStatus?.connected) return;
    qboService.getAccounts().then(setQboExpenseAccountsList).catch(() => {});
    qboService.getAccounts(['Bank', 'Credit Card']).then(setQboBankAccountsList).catch(() => {});
  }, [qboStatus?.connected]);

  useEffect(() => {
    deliveryFleetService.getShopifySettings()
      .then(s => {
        setShopify(s);
        setShopifyForm({ shopifyShopDomain: s.shopifyShopDomain || '', defaultDriverPayoutUsd: s.defaultDriverPayoutUsd || '' });
      })
      .catch(() => {})
      .finally(() => setShopifyLoading(false));
  }, []);

  useEffect(() => {
    r365Service.getStatus()
      .then(s => {
        setR365(s);
        setR365Form({
          r365Subdomain: s.r365Subdomain || '',
          r365Username: s.r365Username || '',
          r365Password: '',
          r365DefaultLocation: s.r365DefaultLocation || '',
          r365DefaultGLAccount: s.r365DefaultGLAccount || '',
        });
      })
      .catch(() => {})
      .finally(() => setR365Loading(false));
  }, []);

  const handleR365Save = async () => {
    setR365Saving(true); setR365Error(''); setR365TestResult(null);
    try {
      const payload = {
        r365Subdomain: r365Form.r365Subdomain.trim(),
        r365Username: r365Form.r365Username.trim(),
        r365DefaultLocation: r365Form.r365DefaultLocation.trim(),
        r365DefaultGLAccount: r365Form.r365DefaultGLAccount.trim(),
      };
      if (r365Form.r365Password) payload.r365Password = r365Form.r365Password;
      await r365Service.saveCredentials(payload);
      const s = await r365Service.getStatus();
      setR365(s);
      setR365Form(f => ({ ...f, r365Password: '' }));
      setR365Saved(true);
      setTimeout(() => setR365Saved(false), 2000);
    } catch {
      setR365Error('Failed to save Restaurant 365 settings.');
    } finally {
      setR365Saving(false);
    }
  };

  const handleR365Test = async () => {
    setR365Testing(true); setR365TestResult(null);
    try {
      const result = await r365Service.testConnection();
      setR365TestResult({ ok: true, message: result.message });
    } catch (err) {
      setR365TestResult({ ok: false, message: err.response?.data?.message || err.message });
    } finally {
      setR365Testing(false);
    }
  };

  const handleShopifySave = async (regenerateSecret = false) => {
    setShopifySaving(true); setShopifyError('');
    try {
      const s = await deliveryFleetService.saveShopifySettings({
        shopifyShopDomain: shopifyForm.shopifyShopDomain.trim(),
        defaultDriverPayoutUsd: shopifyForm.defaultDriverPayoutUsd === '' ? null : Number(shopifyForm.defaultDriverPayoutUsd),
        webhookSecret: shopify?.webhookSecret,
        regenerateSecret,
      });
      setShopify(s);
      setShopifySaved(true);
      setTimeout(() => setShopifySaved(false), 2000);
    } catch {
      setShopifyError('Failed to save Shopify settings.');
    } finally {
      setShopifySaving(false);
    }
  };

  const copyShopify = (label, value) => {
    navigator.clipboard.writeText(value || '');
    setShopifyCopied(label);
    setTimeout(() => setShopifyCopied(''), 1500);
  };

  const handleShopifyTestOrder = async () => {
    setShopifyTesting(true); setShopifyTestResult(null);
    try {
      const result = await deliveryFleetService.sendShopifyTestOrder();
      setShopifyTestResult(result);
    } catch (err) {
      setShopifyTestResult({ ok: false, error: err.response?.data?.error || err.message });
    } finally {
      setShopifyTesting(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qboParam  = params.get('qbo');
    const xeroParam = params.get('xero');

    if (qboParam === 'connected') {
      qboService.getStatus().then(setQboStatus).catch(() => {});
      window.history.replaceState({}, '', window.location.pathname);
    } else if (qboParam === 'error') {
      setQboError(`QuickBooks connection failed: ${params.get('reason') || 'Unknown error'}`);
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (xeroParam === 'connected') {
      xeroService.getStatus().then(setXeroStatus).catch(() => {});
      window.history.replaceState({}, '', window.location.pathname);
    } else if (xeroParam === 'error') {
      setXeroError(`Xero connection failed: ${params.get('reason') || 'Unknown error'}`);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (xeroParam === 'pick-org') {
      try {
        const tenants = JSON.parse(decodeURIComponent(params.get('tenants') || '[]'));
        setXeroOrgPickerTenants(tenants);
      } catch { setXeroError('Failed to parse Xero organisation list.'); }
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleQboConnect = async () => {
    setQboConnecting(true);
    setQboError('');
    try {
      const url = await qboService.getAuthUrl();
      window.location.href = url;
    } catch {
      setQboError('Failed to start QuickBooks connection. Check your QBO configuration.');
      setQboConnecting(false);
    }
  };

  const handleQboDisconnect = async () => {
    setQboDisconnecting(true);
    try {
      await qboService.disconnect();
      setQboStatus({ connected: false });
    } catch {
      setQboError('Failed to disconnect. Please try again.');
    } finally {
      setQboDisconnecting(false);
      setConfirmQboDisconnect(false);
    }
  };

  const handleXeroConnect = async () => {
    setXeroConnecting(true);
    setXeroError('');
    try {
      const url = await xeroService.getAuthUrl();
      window.location.href = url;
    } catch {
      setXeroError('Failed to start Xero connection. Check your Xero configuration.');
      setXeroConnecting(false);
    }
  };

  const handleXeroDisconnect = async () => {
    setXeroDisconnecting(true);
    try {
      await xeroService.disconnect();
      setXeroStatus({ connected: false });
    } catch {
      setXeroError('Failed to disconnect. Please try again.');
    } finally {
      setXeroDisconnecting(false);
      setConfirmXeroDisconnect(false);
    }
  };

  const handleXeroSelectOrg = async (tenant) => {
    setXeroOrgPicking(true);
    try {
      await xeroService.selectTenant(tenant.tenantId, tenant.name);
      setXeroOrgPickerTenants(null);
      const status = await xeroService.getStatus();
      setXeroStatus(status);
    } catch {
      setXeroError('Failed to select Xero organisation. Please try again.');
    } finally {
      setXeroOrgPicking(false);
    }
  };

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm(BLANK_TEMPLATE);
    setLabelTemplateError('');
    setShowLabelTemplateForm(true);
  };

  const openEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({
      name: t.name, isDefault: t.isDefault, storageBanner: t.storageBanner || '',
      storageBannerColor: t.storageBannerColor || 'black', dateFormat: t.dateFormat || 'MM/DD/YYYY',
      dateLabel: t.dateLabel || 'Expiration', casesLabel: t.casesLabel || 'CASES ON PALLET',
      showLogo: t.showLogo !== false, showManufacturedBy: t.showManufacturedBy !== false,
      customFooterNote: t.customFooterNote || '', labelSize: t.labelSize || '4x6',
    });
    setLabelTemplateError('');
    setShowLabelTemplateForm(true);
  };

  const saveLabelTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.name.trim()) { setLabelTemplateError('Name is required.'); return; }
    setLabelTemplateSaving(true);
    setLabelTemplateError('');
    try {
      if (editingTemplate) {
        const updated = await labelService.updateTemplate(editingTemplate.id, templateForm);
        setLabelTemplates(ts => ts.map(t => t.id === updated.id ? updated : t));
      } else {
        const created = await labelService.createTemplate(templateForm);
        setLabelTemplates(ts => [...ts, created]);
      }
      setShowLabelTemplateForm(false);
    } catch {
      setLabelTemplateError('Failed to save template.');
    } finally {
      setLabelTemplateSaving(false);
    }
  };

  const deleteLabelTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await labelService.deleteTemplate(t.id);
      setLabelTemplates(ts => ts.filter(x => x.id !== t.id));
    } catch { /* ignore */ }
  };

  if (!companySettings) {
    return (
      <div className="container mt-4">
        <div className="spinner-border" role="status" />
      </div>
    );
  }

  const handleQboAccountsSave = async (e) => {
    e.preventDefault();
    setQboAccountsSaving(true);
    try {
      await companyService.updateSettings({
        qbPurchaseAccountId: qboPurchaseAccountId || null,
        qbPurchaseAccountName: qboPurchaseAccountId
          ? (qboBankAccountsList.find(a => String(a.id) === String(qboPurchaseAccountId))?.name || null)
          : null,
        qbDefaultExpenseAccountId: qboDefaultExpenseAccountId || null,
        qbDefaultExpenseAccountName: qboDefaultExpenseAccountId
          ? (qboExpenseAccountsList.find(a => String(a.id) === String(qboDefaultExpenseAccountId))?.name || null)
          : null,
        qbSalesTaxAccountId: qboSalesTaxAccountId || null,
        qbSalesTaxAccountName: qboSalesTaxAccountId
          ? (qboExpenseAccountsList.find(a => String(a.id) === String(qboSalesTaxAccountId))?.name || null)
          : null,
      });
      setQboAccountsSuccess(true);
      setTimeout(() => setQboAccountsSuccess(false), 3000);
      refreshPermissions();
    } catch { /* ignore */ }
    finally { setQboAccountsSaving(false); }
  };

  const handleBolGeneralSave = async (e) => {
    e.preventDefault();
    setBolGeneralSaving(true);
    try { await bolService.updateSettings(bolGeneral); setBolGeneralSuccess(true); setTimeout(() => setBolGeneralSuccess(false), 3000); } catch { /* ignore */ }
    finally { setBolGeneralSaving(false); }
  };

  const handleBolDisclaimerSave = async (e) => {
    e.preventDefault();
    setBolDisclaimerSaving(true);
    try { await bolService.updateSettings({ bolShowDisclaimer, bolDisclaimerText }); setBolDisclaimerSuccess(true); setTimeout(() => setBolDisclaimerSuccess(false), 3000); } catch { /* ignore */ }
    finally { setBolDisclaimerSaving(false); }
  };

  const handleBolRefrigSave = async (e) => {
    e.preventDefault();
    setBolRefrigSaving(true);
    try { await bolService.updateSettings({ bolShowRefrigerationSection: bolShowRefrig, bolRefrigerationChecks: bolRefrigChecks }); setBolRefrigSuccess(true); setTimeout(() => setBolRefrigSuccess(false), 3000); } catch { /* ignore */ }
    finally { setBolRefrigSaving(false); }
  };

  const handleBolShipViaSave = async (e) => {
    e.preventDefault();
    setBolShipViaSaving(true);
    try { await bolService.updateSettings({ bolShipViaOptions: bolShipVia }); setBolShipViaSuccess(true); setTimeout(() => setBolShipViaSuccess(false), 3000); } catch { /* ignore */ }
    finally { setBolShipViaSaving(false); }
  };

  const handleNameSave = async (e) => {
    e.preventDefault();
    if (!companyName.trim() || companyName.trim() === companySettings.name) return;
    setNameSaving(true);
    try {
      await companyService.updateSettings({ name: companyName.trim() });
      refreshPermissions();
      setNameSuccess(true);
      setTimeout(() => setNameSuccess(false), 3000);
    } catch {
      // silently ignore — user will see no feedback but won't crash
    } finally {
      setNameSaving(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError('');
    setLogoUploading(true);
    try {
      const { uploadUrl, publicUrl } = await companyService.getLogoUploadUrl(file.name, file.type);
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });
      await companyService.updateSettings({ logoUrl: publicUrl });
      refreshPermissions();
    } catch {
      setLogoError('Upload failed. Please check your file and try again.');
    } finally {
      setLogoUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    setLogoUploading(true);
    try {
      await companyService.updateSettings({ logoUrl: '' });
      refreshPermissions();
    } catch {
      setLogoError('Failed to remove logo.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleNavbarLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNavbarLogoError('');
    setNavbarLogoUploading(true);
    try {
      const { uploadUrl, publicUrl } = await companyService.getLogoUploadUrl(file.name, file.type);
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });
      await companyService.updateSettings({ navbarLogoUrl: publicUrl });
      refreshPermissions();
    } catch {
      setNavbarLogoError('Upload failed. Please check your file and try again.');
    } finally {
      setNavbarLogoUploading(false);
      if (navbarFileInputRef.current) navbarFileInputRef.current.value = '';
    }
  };

  const handleRemoveNavbarLogo = async () => {
    setNavbarLogoUploading(true);
    try {
      await companyService.updateSettings({ navbarLogoUrl: '' });
      refreshPermissions();
    } catch {
      setNavbarLogoError('Failed to remove logo.');
    } finally {
      setNavbarLogoUploading(false);
    }
  };

  const handleContactSave = async (e) => {
    e.preventDefault();
    setContactSaving(true);
    try {
      await companyService.updateSettings(contact);
      refreshPermissions();
      setContactSuccess(true);
      setTimeout(() => setContactSuccess(false), 3000);
    } catch {
      // ignore
    } finally {
      setContactSaving(false);
    }
  };

  const handleThresholdSave = async (e) => {
    e.preventDefault();
    const val = Number(deviationThreshold);
    if (!Number.isFinite(val) || val < 0) return;
    setThresholdSaving(true);
    try {
      await companyService.updateSettings({ ingredientDeviationThreshold: val });
      refreshPermissions();
      setThresholdSuccess(true);
      setTimeout(() => setThresholdSuccess(false), 3000);
    } catch {
      // ignore
    } finally {
      setThresholdSaving(false);
    }
  };

  const handleToggle = async (value) => {
    setRequireIngredientQty(value);
    setToggleSaving(true);
    try {
      await companyService.updateSettings({ requireIngredientQty: value });
      refreshPermissions();
    } catch {
      setRequireIngredientQty(!value); // revert
    } finally {
      setToggleSaving(false);
    }
  };

  const handleLotCodeFormatSave = async (e) => {
    e.preventDefault();
    setLotCodeFormatSaving(true);
    try {
      // Save null when the user has restored the default, so the DB stays clean
      const valueToSave = lotCodeFormat === DEFAULT_LOT_CODE_FORMAT ? null : lotCodeFormat;
      await companyService.updateSettings({ lotCodeFormat: valueToSave });
      refreshPermissions();
      setLotCodeFormatSuccess(true);
      setTimeout(() => setLotCodeFormatSuccess(false), 3000);
    } catch {
      // ignore
    } finally {
      setLotCodeFormatSaving(false);
    }
  };

  const insertToken = (token) => {
    setLotCodeFormat(f => f + token);
  };

  const handleLotCodeKeySave = async (e) => {
    e.preventDefault();
    setLotCodeKeySaving(true);
    try {
      // Save null when the user has restored the default, so the DB stays clean
      const valueToSave = lotCodeKey === DEFAULT_LOT_CODE_KEY ? null : lotCodeKey;
      await companyService.updateSettings({ lotCodeKey: valueToSave });
      refreshPermissions();
      setLotCodeKeySuccess(true);
      setTimeout(() => setLotCodeKeySuccess(false), 3000);
    } catch {
      // ignore
    } finally {
      setLotCodeKeySaving(false);
    }
  };

  const handleUsdaEstNumberSave = async (e) => {
    e.preventDefault();
    setUsdaEstNumberSaving(true);
    try {
      await companyService.updateSettings({ usdaEstablishmentNumber: usdaEstablishmentNumber.trim() || null });
      refreshPermissions();
      setUsdaEstNumberSuccess(true);
      setTimeout(() => setUsdaEstNumberSuccess(false), 3000);
    } catch {
      // ignore
    } finally {
      setUsdaEstNumberSaving(false);
    }
  };

  const handleRequireFatSubProfileToggle = async (checked) => {
    setRequireFatSubProfile(checked);
    setFatSubProfileSaving(true);
    try {
      await companyService.updateSettings({ requireFatSubProfile: checked });
      setFatSubProfileSuccess(true);
      setTimeout(() => setFatSubProfileSuccess(false), 3000);
    } catch {
      setRequireFatSubProfile(!checked); // revert on failure
    } finally {
      setFatSubProfileSaving(false);
    }
  };

  const handlePayPeriodSave = async (e) => {
    e.preventDefault();
    setPayPeriodSaving(true);
    try {
      await companyService.updateSettings({ payPeriodType });
      refreshPermissions();
      setPayPeriodSuccess(true);
      setTimeout(() => setPayPeriodSuccess(false), 3000);
    } catch {
      // ignore
    } finally {
      setPayPeriodSaving(false);
    }
  };

  const handleOpenBillingPortal = async () => {
    setPortalLoading(true);
    setPortalError('');
    try {
      const url = await billingService.getMyPortalUrl();
      window.location.href = url;
    } catch (err) {
      setPortalError(err.response?.data?.error || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  const currentLogo = companySettings.logoUrl || process.env.REACT_APP_LOGO_URL || null;
  const currentNavbarLogo = companySettings.navbarLogoUrl || null;

  return (
    <div className="container mt-4" style={{ maxWidth: 680 }}>
      <h4 className="mb-4">Company Settings</h4>

      {/* ── Company Name ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-3">Company Name</h6>
          <form onSubmit={handleNameSave} className="d-flex gap-2 align-items-center">
            <input
              type="text"
              className="form-control"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              style={{ maxWidth: 360 }}
              required
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={nameSaving || !companyName.trim() || companyName.trim() === companySettings.name}
            >
              {nameSaving ? (
                <span className="spinner-border spinner-border-sm" role="status" />
              ) : 'Save'}
            </button>
            {nameSuccess && <span className="text-success small">Saved</span>}
          </form>
        </div>
      </div>

      {/* ── Print Logo ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-1">Print Logo</h6>
          <p className="text-muted mb-3" style={{ fontSize: 12 }}>Shown on printed forms (BOL, traceability reports). Use a dark or full-color version that shows well on white paper.</p>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            {currentLogo ? (
              <img
                src={currentLogo}
                alt="Print logo"
                style={{ height: 56, maxWidth: 200, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 4, padding: 4 }}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center bg-light text-muted"
                style={{ height: 56, width: 160, border: '1px dashed #dee2e6', borderRadius: 4, fontSize: 12 }}
              >
                No logo set
              </div>
            )}
            <div className="d-flex flex-column gap-1">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={logoUploading}
              >
                {logoUploading ? (
                  <><span className="spinner-border spinner-border-sm me-1" role="status" />Uploading…</>
                ) : (
                  currentLogo ? 'Replace' : 'Upload'
                )}
              </button>
              {currentLogo && (
                <button className="btn btn-outline-danger btn-sm" onClick={handleRemoveLogo} disabled={logoUploading}>
                  Remove
                </button>
              )}
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleLogoChange} />
          {logoError && <div className="alert alert-danger mt-2 py-2 px-3" style={{ fontSize: 13 }}>{logoError}</div>}
        </div>
      </div>

      {/* ── Navbar Logo ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-1">Navbar Logo</h6>
          <p className="text-muted mb-3" style={{ fontSize: 12 }}>Shown in the navigation bar (dark background). Use a light or white version. If not set, the print logo is used as fallback.</p>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            {currentNavbarLogo ? (
              <img
                src={currentNavbarLogo}
                alt="Navbar logo"
                style={{ height: 56, maxWidth: 200, objectFit: 'contain', background: '#1b2638', borderRadius: 4, padding: 8 }}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center text-muted"
                style={{ height: 56, width: 160, background: '#1b2638', borderRadius: 4, fontSize: 12, color: '#aaa' }}
              >
                <span style={{ color: '#aaa' }}>No logo set</span>
              </div>
            )}
            <div className="d-flex flex-column gap-1">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => navbarFileInputRef.current?.click()}
                disabled={navbarLogoUploading}
              >
                {navbarLogoUploading ? (
                  <><span className="spinner-border spinner-border-sm me-1" role="status" />Uploading…</>
                ) : (
                  currentNavbarLogo ? 'Replace' : 'Upload'
                )}
              </button>
              {currentNavbarLogo && (
                <button className="btn btn-outline-danger btn-sm" onClick={handleRemoveNavbarLogo} disabled={navbarLogoUploading}>
                  Remove
                </button>
              )}
            </div>
          </div>
          <input ref={navbarFileInputRef} type="file" accept="image/*" className="d-none" onChange={handleNavbarLogoChange} />
          {navbarLogoError && <div className="alert alert-danger mt-2 py-2 px-3" style={{ fontSize: 13 }}>{navbarLogoError}</div>}
        </div>
      </div>

      {/* ── Contact & Address ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-3">Contact & Address</h6>
          <form onSubmit={handleContactSave}>
            <div className="row g-2 mb-2">
              <div className="col-12">
                <label className="form-label mb-1" style={{ fontSize: 13 }}>Address Line 1</label>
                <input type="text" className="form-control form-control-sm" value={contact.addressLine1} onChange={e => setContact(c => ({ ...c, addressLine1: e.target.value }))} placeholder="123 Main St" />
              </div>
              <div className="col-12">
                <label className="form-label mb-1" style={{ fontSize: 13 }}>Address Line 2</label>
                <input type="text" className="form-control form-control-sm" value={contact.addressLine2} onChange={e => setContact(c => ({ ...c, addressLine2: e.target.value }))} placeholder="Suite 100" />
              </div>
              <div className="col-md-5">
                <label className="form-label mb-1" style={{ fontSize: 13 }}>City</label>
                <input type="text" className="form-control form-control-sm" value={contact.city} onChange={e => setContact(c => ({ ...c, city: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label mb-1" style={{ fontSize: 13 }}>State / Province</label>
                <input type="text" className="form-control form-control-sm" value={contact.stateProvince} onChange={e => setContact(c => ({ ...c, stateProvince: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label mb-1" style={{ fontSize: 13 }}>Postal Code</label>
                <input type="text" className="form-control form-control-sm" value={contact.postalCode} onChange={e => setContact(c => ({ ...c, postalCode: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label mb-1" style={{ fontSize: 13 }}>Country</label>
                <input type="text" className="form-control form-control-sm" value={contact.country} onChange={e => setContact(c => ({ ...c, country: e.target.value }))} placeholder="US" />
              </div>
              <div className="col-md-6">
                <label className="form-label mb-1" style={{ fontSize: 13 }}>Phone</label>
                <input type="tel" className="form-control form-control-sm" value={contact.phone} onChange={e => setContact(c => ({ ...c, phone: e.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label mb-1" style={{ fontSize: 13 }}>Email</label>
                <input type="email" className="form-control form-control-sm" value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))} />
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 mt-1">
              <button type="submit" className="btn btn-primary btn-sm" disabled={contactSaving}>
                {contactSaving ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Save'}
              </button>
              {contactSuccess && <span className="text-success small">Saved</span>}
            </div>
          </form>
        </div>
      </div>

      {/* ── Ingredient Quantity ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-1">Require Ingredient Quantities</h6>
          <p className="text-muted mb-3" style={{ fontSize: 13 }}>
            When enabled, ingredient quantities are required when logging production.
          </p>
          <div className="d-flex gap-2">
            <button
              className={`btn btn-sm ${requireIngredientQty ? 'btn-success' : 'btn-outline-secondary'}`}
              onClick={() => !requireIngredientQty && handleToggle(true)}
              disabled={toggleSaving}
            >
              Required
            </button>
            <button
              className={`btn btn-sm ${!requireIngredientQty ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => requireIngredientQty && handleToggle(false)}
              disabled={toggleSaving}
            >
              Optional
            </button>
            {toggleSaving && <span className="spinner-border spinner-border-sm align-self-center" role="status" />}
          </div>
        </div>
      </div>

      {/* ── Lot Code Format ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-1">Lot Code Format</h6>
          <p className="text-muted mb-3" style={{ fontSize: 13 }}>
            Define the format used to auto-generate lot codes when starting a new production batch.
            Click the token buttons to insert date parts, or type any static prefix/suffix directly.
          </p>
          <form onSubmit={handleLotCodeFormatSave}>
            <div className="d-flex flex-wrap gap-1 mb-2">
              {LOT_CODE_TOKENS.map(({ token, label }) => (
                <button
                  key={token}
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => insertToken(token)}
                  title={label}
                >
                  {token}
                </button>
              ))}
              <button
                type="button"
                className="btn btn-outline-danger btn-sm ms-2"
                onClick={() => setLotCodeFormat(DEFAULT_LOT_CODE_FORMAT)}
                title="Reset to default"
              >
                Reset
              </button>
            </div>
            <div className="d-flex gap-2 align-items-center mb-2">
              <input
                type="text"
                className="form-control form-control-sm font-monospace"
                style={{ maxWidth: 300 }}
                value={lotCodeFormat}
                onChange={e => setLotCodeFormat(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-primary btn-sm" disabled={lotCodeFormatSaving}>
                {lotCodeFormatSaving ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Save'}
              </button>
              {lotCodeFormatSuccess && <span className="text-success small">Saved</span>}
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Preview: <strong className="font-monospace">{generateLotCode(lotCodeFormat)}</strong>
            </div>
          </form>

          <hr className="my-3" />

          <h6 className="card-title mb-1">Lot Number Key</h6>
          <p className="text-muted mb-2" style={{ fontSize: 13 }}>
            Plain-language legend explaining the format above — auto-populated into new technical
            sheets alongside the format itself.
          </p>
          <form onSubmit={handleLotCodeKeySave}>
            <div className="d-flex gap-2 align-items-start mb-2">
              <textarea
                className="form-control form-control-sm"
                rows={2}
                style={{ maxWidth: 480 }}
                value={lotCodeKey}
                onChange={e => setLotCodeKey(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-primary btn-sm" disabled={lotCodeKeySaving}>
                {lotCodeKeySaving ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Save'}
              </button>
              {lotCodeKeySuccess && <span className="text-success small align-self-center">Saved</span>}
            </div>
          </form>
        </div>
      </div>

      {/* ── Ingredient Deviation Threshold ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-1">Ingredient Quantity Deviation Threshold</h6>
          <p className="text-muted mb-3" style={{ fontSize: 13 }}>
            When the quantity entered for an ingredient deviates from the recipe by more than this percentage,
            the user will be warned and asked to provide a justification note.
          </p>
          <form onSubmit={handleThresholdSave} className="d-flex gap-2 align-items-center">
            <div className="input-group" style={{ maxWidth: 180 }}>
              <input
                type="number"
                className="form-control"
                min="0"
                max="100"
                step="0.1"
                value={deviationThreshold}
                onChange={e => setDeviationThreshold(e.target.value)}
                required
              />
              <span className="input-group-text">%</span>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={thresholdSaving}
            >
              {thresholdSaving ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Save'}
            </button>
            {thresholdSuccess && <span className="text-success small">Saved</span>}
          </form>
        </div>
      </div>

      {/* ── BOL General ── */}
      <h6 className="text-muted fw-semibold mt-4 mb-2" style={{ fontSize: 12, letterSpacing: 1 }}>BILL OF LADING</h6>
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-3">BOL General Settings</h6>
          <form onSubmit={handleBolGeneralSave}>
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <label className="form-label small mb-1">BOL Number Prefix</label>
                <input className="form-control form-control-sm font-monospace" value={bolGeneral.bolNumberPrefix} onChange={e => setBolGeneral(g => ({ ...g, bolNumberPrefix: e.target.value }))} placeholder="BOL" />
                <div className="text-muted" style={{ fontSize: 11 }}>Preview: {bolGeneral.bolNumberPrefix || 'BOL'}-{new Date().getFullYear()}-0001</div>
              </div>
              <div className="col-md-4">
                <label className="form-label small mb-1">Pallet ID Format</label>
                <input className="form-control form-control-sm font-monospace" value={bolGeneral.bolPalletIdFormat} onChange={e => setBolGeneral(g => ({ ...g, bolPalletIdFormat: e.target.value }))} placeholder="{N}" />
                <div className="text-muted" style={{ fontSize: 11 }}>Tokens: {'{N}'} {'{NN}'} {'{NNN}'}. Preview: {(bolGeneral.bolPalletIdFormat || '{N}').replace(/{NNN}/g, '001').replace(/{NN}/g, '01').replace(/{N}/g, '1')}</div>
              </div>
              <div className="col-md-2">
                <label className="form-label small mb-1">Default Weight Unit</label>
                <div className="d-flex gap-2">
                  {['lb', 'kg'].map(u => (
                    <button key={u} type="button" className={`btn btn-sm ${bolGeneral.bolDefaultWeightUnit === u ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setBolGeneral(g => ({ ...g, bolDefaultWeightUnit: u }))}>{u}</button>
                  ))}
                </div>
              </div>
              <div className="col-md-2">
                <label className="form-label small mb-1">Pallet Weight</label>
                <div className="input-group input-group-sm">
                  <input
                    type="number" min="0" step="0.001" className="form-control"
                    value={bolGeneral.palletWeightKg}
                    onChange={e => setBolGeneral(g => ({ ...g, palletWeightKg: e.target.value }))}
                    placeholder="18.14"
                  />
                  <span className="input-group-text">kg</span>
                </div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  Stored in kg — 40 lb ≈ 18.14 kg. Used for BOL gross calc.
                </div>
              </div>
              <div className="col-12">
                <label className="form-label small mb-1">Document Title</label>
                <input className="form-control form-control-sm" value={bolGeneral.bolTitle} onChange={e => setBolGeneral(g => ({ ...g, bolTitle: e.target.value }))} placeholder="STRAIGHT BILL OF LADING (NON-NEGOTIABLE)" />
              </div>
              <div className="col-12">
                <label className="form-label small mb-1">Document Subtitle <span className="text-muted">(e.g. "For Refrigerated Transport")</span></label>
                <input className="form-control form-control-sm" value={bolGeneral.bolSubtitle} onChange={e => setBolGeneral(g => ({ ...g, bolSubtitle: e.target.value }))} placeholder="Optional subtitle" />
              </div>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <button type="submit" className="btn btn-primary btn-sm" disabled={bolGeneralSaving}>
                {bolGeneralSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
              </button>
              {bolGeneralSuccess && <span className="text-success small">Saved</span>}
            </div>
          </form>
        </div>
      </div>

      {/* ── BOL Ship Via ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-1">Ship Via Options</h6>
          <form onSubmit={handleBolShipViaSave}>
            <ul className="list-group list-group-flush mb-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
              {bolShipVia.map((opt, i) => (
                <li key={i} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1">
                  <span style={{ fontSize: 13 }}>{opt}</span>
                  <button type="button" className="btn btn-outline-danger btn-sm py-0 px-1" onClick={() => setBolShipVia(v => v.filter((_, j) => j !== i))}>×</button>
                </li>
              ))}
            </ul>
            <div className="d-flex gap-2 mb-2">
              <input className="form-control form-control-sm" value={newShipVia} onChange={e => setNewShipVia(e.target.value)} placeholder="Add option…" />
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { if (newShipVia.trim()) { setBolShipVia(v => [...v, newShipVia.trim()]); setNewShipVia(''); } }}>Add</button>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <button type="submit" className="btn btn-primary btn-sm" disabled={bolShipViaSaving}>
                {bolShipViaSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
              </button>
              {bolShipViaSuccess && <span className="text-success small">Saved</span>}
            </div>
          </form>
        </div>
      </div>

      {/* ── BOL Disclaimer ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-1">Disclaimer Text</h6>
          <p className="text-muted mb-3" style={{ fontSize: 13 }}>Printed below the items table on the BOL.</p>
          <form onSubmit={handleBolDisclaimerSave}>
            <div className="form-check form-switch mb-2">
              <input className="form-check-input" type="checkbox" id="bolShowDisclaimer" checked={bolShowDisclaimer} onChange={e => setBolShowDisclaimer(e.target.checked)} />
              <label className="form-check-label small" htmlFor="bolShowDisclaimer">Show disclaimer on printed BOL</label>
            </div>
            <textarea className="form-control form-control-sm mb-2" rows={4} value={bolDisclaimerText} onChange={e => setBolDisclaimerText(e.target.value)} disabled={!bolShowDisclaimer} placeholder="The goods described above were loaded in apparent good condition…" />
            <div className="d-flex gap-2 align-items-center">
              <button type="submit" className="btn btn-primary btn-sm" disabled={bolDisclaimerSaving}>
                {bolDisclaimerSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
              </button>
              {bolDisclaimerSuccess && <span className="text-success small">Saved</span>}
            </div>
          </form>
        </div>
      </div>

      {/* ── BOL Refrigeration Checks ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-1">Refrigeration / Loading Checks</h6>
          <p className="text-muted mb-3" style={{ fontSize: 13 }}>Configure the checks shown at the bottom of the BOL. Enable/disable or edit labels to match your requirements.</p>
          <form onSubmit={handleBolRefrigSave}>
            <div className="form-check form-switch mb-3">
              <input className="form-check-input" type="checkbox" id="bolShowRefrig" checked={bolShowRefrig} onChange={e => setBolShowRefrig(e.target.checked)} />
              <label className="form-check-label small" htmlFor="bolShowRefrig">Show checks section on printed BOL</label>
            </div>
            {bolRefrigChecks.map((check, i) => (
              <div key={check.id} className="d-flex gap-2 align-items-center mb-2 flex-wrap">
                <div className="form-check mb-0" style={{ minWidth: 20 }}>
                  <input className="form-check-input" type="checkbox" checked={check.enabled} onChange={e => setBolRefrigChecks(cs => cs.map((c, j) => j === i ? { ...c, enabled: e.target.checked } : c))} />
                </div>
                <input className="form-control form-control-sm" style={{ maxWidth: 220 }} value={check.label} onChange={e => setBolRefrigChecks(cs => cs.map((c, j) => j === i ? { ...c, label: e.target.value } : c))} />
                <span className="badge bg-secondary" style={{ fontSize: 10 }}>{check.type}</span>
                {check.type === 'text' && (
                  <input className="form-control form-control-sm" style={{ maxWidth: 180 }} value={check.defaultValue || ''} placeholder="Default value" onChange={e => setBolRefrigChecks(cs => cs.map((c, j) => j === i ? { ...c, defaultValue: e.target.value } : c))} />
                )}
                <button type="button" className="btn btn-outline-danger btn-sm py-0 px-1 ms-auto" onClick={() => setBolRefrigChecks(cs => cs.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
            <div className="d-flex gap-2 mt-2 flex-wrap">
              {[['text_input', 'Text Input'], ['yes_no', 'Yes/No'], ['temperature', 'Temperature'], ['number', 'Number'], ['signature_line', 'Signature']].map(([type, label]) => (
                <button key={type} type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setBolRefrigChecks(cs => [...cs, { id: `custom_${Date.now()}`, label: label, type, enabled: true }])}>+ {label}</button>
              ))}
            </div>
            <div className="d-flex gap-2 align-items-center mt-3">
              <button type="submit" className="btn btn-primary btn-sm" disabled={bolRefrigSaving}>
                {bolRefrigSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
              </button>
              {bolRefrigSuccess && <span className="text-success small">Saved</span>}
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setBolRefrigChecks(DEFAULT_REFRIG_CHECKS)}>Reset to default</button>
            </div>
          </form>
        </div>
      </div>

      {/* ── Labels ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-3">Labels</h6>

          {/* Template list */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted small">Pallet label templates</span>
            <button className="btn btn-sm btn-outline-primary" onClick={openNewTemplate}>+ New template</button>
          </div>

          {labelTemplatesLoading ? (
            <div className="spinner-border spinner-border-sm" />
          ) : labelTemplates.length === 0 ? (
            <p className="text-muted small mb-0">No templates yet. Create one to get started.</p>
          ) : (
            <table className="table table-sm mb-3">
              <thead><tr><th>Name</th><th>Banner</th><th>Size</th><th>Default</th><th></th></tr></thead>
              <tbody>
                {labelTemplates.map(t => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td className="text-muted small">{t.storageBanner || '—'}</td>
                    <td className="text-muted small">{t.labelSize}</td>
                    <td>{t.isDefault ? <span className="badge bg-success">Default</span> : null}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openEditTemplate(t)}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => deleteLabelTemplate(t)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Template form */}
          {showLabelTemplateForm && (
            <form onSubmit={saveLabelTemplate} className="border rounded p-3 mt-2">
              <h6 className="mb-3">{editingTemplate ? 'Edit template' : 'New template'}</h6>
              {labelTemplateError && <div className="alert alert-danger py-1 small">{labelTemplateError}</div>}

              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label">Template name</label>
                  <input className="form-control form-control-sm" value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Label size</label>
                  <select className="form-select form-select-sm" value={templateForm.labelSize} onChange={e => setTemplateForm(f => ({ ...f, labelSize: e.target.value }))}>
                    <option value="4x6">4" × 6" (default)</option>
                    <option value="letter">Letter (8.5" × 11")</option>
                  </select>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="tplDefault" checked={templateForm.isDefault} onChange={e => setTemplateForm(f => ({ ...f, isDefault: e.target.checked }))} />
                    <label className="form-check-label" htmlFor="tplDefault">Set as default</label>
                  </div>
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-8">
                  <label className="form-label">Storage banner <small className="text-muted">(leave blank to hide)</small></label>
                  <input className="form-control form-control-sm" placeholder="e.g. KEEP FROZEN — Store at 0°F (-18°C) or Below" value={templateForm.storageBanner} onChange={e => setTemplateForm(f => ({ ...f, storageBanner: e.target.value }))} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Banner color</label>
                  <select className="form-select form-select-sm" value={templateForm.storageBannerColor} onChange={e => setTemplateForm(f => ({ ...f, storageBannerColor: e.target.value }))}>
                    <option value="black">Black</option>
                    <option value="blue">Blue</option>
                    <option value="red">Red</option>
                    <option value="green">Green</option>
                    <option value="orange">Orange</option>
                  </select>
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-4">
                  <label className="form-label">Date label</label>
                  <input className="form-control form-control-sm" placeholder="e.g. Expiration" value={templateForm.dateLabel} onChange={e => setTemplateForm(f => ({ ...f, dateLabel: e.target.value }))} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Date format</label>
                  <select className="form-select form-select-sm" value={templateForm.dateFormat} onChange={e => setTemplateForm(f => ({ ...f, dateFormat: e.target.value }))}>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Cases label</label>
                  <input className="form-control form-control-sm" placeholder="CASES ON PALLET" value={templateForm.casesLabel} onChange={e => setTemplateForm(f => ({ ...f, casesLabel: e.target.value }))} />
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-12">
                  <label className="form-label">Footer note <small className="text-muted">(optional)</small></label>
                  <input className="form-control form-control-sm" placeholder="Optional extra line at the bottom of the label" value={templateForm.customFooterNote} onChange={e => setTemplateForm(f => ({ ...f, customFooterNote: e.target.value }))} />
                </div>
              </div>

              <div className="d-flex gap-3 mb-3">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="tplLogo" checked={templateForm.showLogo} onChange={e => setTemplateForm(f => ({ ...f, showLogo: e.target.checked }))} />
                  <label className="form-check-label" htmlFor="tplLogo">Show logo</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="tplMfg" checked={templateForm.showManufacturedBy} onChange={e => setTemplateForm(f => ({ ...f, showManufacturedBy: e.target.checked }))} />
                  <label className="form-check-label" htmlFor="tplMfg">Show Manufactured By</label>
                </div>
              </div>

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-sm btn-primary" disabled={labelTemplateSaving}>
                  {labelTemplateSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save template'}
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowLabelTemplateForm(false)}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* ── Food Safety / Regulatory ── */}
      <h6 className="text-muted fw-semibold mt-4 mb-2" style={{ fontSize: 12, letterSpacing: 1 }}>FOOD SAFETY / REGULATORY</h6>
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-1">USDA/FSIS Establishment Number</h6>
          <p className="text-muted mb-3" style={{ fontSize: 12 }}>
            Reference only — displayed for your team's convenience. The actual mark of
            inspection artwork (with this number baked in) must be uploaded to the Label
            Assets library and used on any USDA-regulated product's label; approval is
            blocked until it's present.
          </p>
          <form onSubmit={handleUsdaEstNumberSave}>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 260 }}
                placeholder="e.g. EST. 12345"
                value={usdaEstablishmentNumber}
                onChange={e => setUsdaEstablishmentNumber(e.target.value)}
              />
              <button type="submit" className="btn btn-sm btn-primary" disabled={usdaEstNumberSaving}>
                {usdaEstNumberSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
              </button>
              {usdaEstNumberSuccess && <span className="text-success small">Saved</span>}
            </div>
          </form>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-1">Nutrition Label — Fat Sub-Profile Requirement</h6>
          <p className="text-muted mb-3" style={{ fontSize: 12 }}>
            When on, approving a nutrition label is blocked until every fat-contributing
            ingredient has saturated, trans, polyunsaturated, and monounsaturated fat entered.
            Poly/mono fat are voluntary nutrients under FDA labeling rules (not required on the
            label), so turn this off if your ingredient spec sheets don't carry that data — sat
            and trans fat are still required as usual, and the check that catches genuinely
            inconsistent data (sub-fats adding up to more than total fat) always stays on
            regardless of this setting.
          </p>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="requireFatSubProfile"
              checked={requireFatSubProfile}
              disabled={fatSubProfileSaving}
              onChange={e => handleRequireFatSubProfileToggle(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="requireFatSubProfile">
              Require complete fat sub-profile before approval
            </label>
            {fatSubProfileSaving && <span className="spinner-border spinner-border-sm ms-2" />}
            {fatSubProfileSuccess && <span className="text-success small ms-2">Saved</span>}
          </div>
        </div>
      </div>

      {/* ── Time Tracking ── */}
      <h6 className="text-muted fw-semibold mt-4 mb-2" style={{ fontSize: 12, letterSpacing: 1 }}>TIME TRACKING</h6>
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-1">Pay Period Type</h6>
          <p className="text-muted mb-3" style={{ fontSize: 12 }}>
            Controls how pay periods are calculated in Time Tracking reports and the Reports page period selector.
          </p>
          <form onSubmit={handlePayPeriodSave}>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: 260 }}
                value={payPeriodType}
                onChange={e => setPayPeriodType(e.target.value)}
              >
                <option value="semi_monthly">Semi-monthly (1–15, 16–end)</option>
                <option value="bi_weekly">Bi-weekly (every 2 weeks)</option>
                <option value="weekly">Weekly (every 7 days)</option>
                <option value="monthly">Monthly (1st–end of month)</option>
              </select>
              <button type="submit" className="btn btn-sm btn-primary" disabled={payPeriodSaving}>
                {payPeriodSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
              </button>
              {payPeriodSuccess && <span className="text-success small">Saved</span>}
            </div>
          </form>
        </div>
      </div>

      {/* ── Accounting Integration ── */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-3">Accounting Integration</h6>

          {(qboError || xeroError) && (
            <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: 13 }}>
              {qboError || xeroError}
              <button type="button" className="btn-close float-end" style={{ fontSize: 10 }} onClick={() => { setQboError(''); setXeroError(''); }} />
            </div>
          )}

          {/* Xero org picker — shown when user has multiple Xero organisations */}
          {xeroOrgPickerTenants && (
            <div className="alert alert-info py-3 px-3 mb-3" style={{ fontSize: 13 }}>
              <div className="fw-semibold mb-2">Multiple Xero organisations found. Select one to connect:</div>
              <div className="d-flex flex-wrap gap-2">
                {xeroOrgPickerTenants.map(t => (
                  <button
                    key={t.tenantId}
                    className="btn btn-sm btn-outline-primary"
                    disabled={xeroOrgPicking}
                    onClick={() => handleXeroSelectOrg(t)}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="row g-3">
            {/* QuickBooks Online card */}
            <div className="col-md-6">
              <div className="border rounded p-3 h-100" style={{ borderColor: qboStatus?.connected ? '#198754' : '#dee2e6' }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <strong style={{ fontSize: 14 }}>QuickBooks Online</strong>
                  {qboLoading
                    ? <span className="spinner-border spinner-border-sm text-secondary ms-auto" />
                    : qboStatus?.connected
                      ? <span className="badge bg-success ms-auto" style={{ fontSize: 10 }}>Connected</span>
                      : <span className="badge bg-secondary ms-auto" style={{ fontSize: 10 }}>Not connected</span>
                  }
                </div>

                {!qboLoading && qboStatus?.connected && (
                  <>
                    <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                      Realm ID: {qboStatus.realmId}
                      {qboStatus.environment === 'sandbox' && (
                        <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 10 }}>Sandbox</span>
                      )}
                    </div>
                    {qboStatus.needsReconnect && (
                      <div className="text-warning mb-2" style={{ fontSize: 12 }}>⚠ Token expired — reconnect to continue</div>
                    )}
                    <div className="d-flex gap-2 mt-3 flex-wrap">
                      <button className="btn btn-sm btn-outline-primary" onClick={() => navigate('/admin/qbo/sync?tab=customers')}>Sync</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => setConfirmQboDisconnect(true)} disabled={qboDisconnecting}>
                        {qboDisconnecting ? <span className="spinner-border spinner-border-sm" /> : 'Disconnect'}
                      </button>
                    </div>

                    {/* Expense push defaults — docs/RECEIPT_SCAN_MODULE.md's Phase 2 */}
                    <form onSubmit={handleQboAccountsSave} className="mt-3 pt-3 border-top">
                      <div className="fw-semibold mb-2" style={{ fontSize: 12 }}>Receipt Scan → QuickBooks</div>
                      <div className="mb-2">
                        <label className="form-label mb-1" style={{ fontSize: 11 }}>Purchase Account (bank/card the expense posts against)</label>
                        <SearchableSelect
                          options={qboBankAccountsList.map(a => ({ value: a.id, label: a.name }))}
                          value={qboPurchaseAccountId}
                          onChange={setQboPurchaseAccountId}
                          placeholder="Select an account…"
                          size="sm"
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label mb-1" style={{ fontSize: 11 }}>Default Expense Account (used when an ingredient has none of its own)</label>
                        <SearchableSelect
                          options={qboExpenseAccountsList.map(a => ({ value: a.id, label: a.name }))}
                          value={qboDefaultExpenseAccountId}
                          onChange={setQboDefaultExpenseAccountId}
                          placeholder="Select an account…"
                          size="sm"
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label mb-1" style={{ fontSize: 11 }}>Sales Tax Expense Account (for the tax line on a scanned receipt)</label>
                        <SearchableSelect
                          options={qboExpenseAccountsList.map(a => ({ value: a.id, label: a.name }))}
                          value={qboSalesTaxAccountId}
                          onChange={setQboSalesTaxAccountId}
                          placeholder="Select an account…"
                          size="sm"
                        />
                      </div>
                      <button type="submit" className="btn btn-sm btn-outline-primary" disabled={qboAccountsSaving}>
                        {qboAccountsSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
                      </button>
                      {qboAccountsSuccess && <span className="text-success ms-2" style={{ fontSize: 12 }}>Saved</span>}
                    </form>
                  </>
                )}
                {!qboLoading && !qboStatus?.connected && (
                  <>
                    <p className="text-muted mb-3" style={{ fontSize: 12 }}>Sync customers, products and create invoices from confirmed orders.</p>
                    <button className="btn btn-sm btn-outline-primary" onClick={handleQboConnect} disabled={qboConnecting}>
                      {qboConnecting ? <><span className="spinner-border spinner-border-sm me-1" />Connecting…</> : 'Connect to QuickBooks'}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Xero card */}
            <div className="col-md-6">
              <div className="border rounded p-3 h-100" style={{ borderColor: xeroStatus?.connected ? '#198754' : '#dee2e6' }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <strong style={{ fontSize: 14 }}>Xero</strong>
                  {xeroLoading
                    ? <span className="spinner-border spinner-border-sm text-secondary ms-auto" />
                    : xeroStatus?.connected
                      ? <span className="badge bg-success ms-auto" style={{ fontSize: 10 }}>Connected</span>
                      : <span className="badge bg-secondary ms-auto" style={{ fontSize: 10 }}>Not connected</span>
                  }
                </div>

                {!xeroLoading && xeroStatus?.connected && (
                  <>
                    <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                      {xeroStatus.orgName}
                    </div>
                    {xeroStatus.connectedAt && (
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Since {new Date(xeroStatus.connectedAt).toLocaleDateString()}
                      </div>
                    )}
                    {xeroStatus.needsReconnect && (
                      <div className="text-warning mb-2" style={{ fontSize: 12 }}>⚠ Token expired — reconnect to continue</div>
                    )}
                    <div className="d-flex gap-2 mt-3 flex-wrap">
                      <button className="btn btn-sm btn-outline-primary" onClick={() => navigate('/admin/xero/sync')}>Sync</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => setConfirmXeroDisconnect(true)} disabled={xeroDisconnecting}>
                        {xeroDisconnecting ? <span className="spinner-border spinner-border-sm" /> : 'Disconnect'}
                      </button>
                    </div>
                  </>
                )}
                {!xeroLoading && !xeroStatus?.connected && (
                  <>
                    <p className="text-muted mb-3" style={{ fontSize: 12 }}>Sync contacts, items and create invoices from confirmed orders.</p>
                    <button className="btn btn-sm btn-outline-primary" onClick={handleXeroConnect} disabled={xeroConnecting}>
                      {xeroConnecting ? <><span className="spinner-border spinner-border-sm me-1" />Connecting…</> : 'Connect to Xero'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Restaurant 365 — outbound expense push only, manual username/password (no OAuth) */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex align-items-center gap-2 mb-1">
            <h6 className="card-title mb-0">Restaurant 365</h6>
            {!r365Loading && (
              r365?.connected
                ? <span className="badge bg-success" style={{ fontSize: 10 }}>Connected</span>
                : <span className="badge bg-secondary" style={{ fontSize: 10 }}>Not connected</span>
            )}
          </div>
          <p className="text-muted mb-3" style={{ fontSize: 12 }}>
            Push a confirmed Ingredient Receiving scan to Restaurant 365 as an AP Invoice, instead
            of (or alongside, later) QuickBooks/Xero. Outbound only — quickLOG cannot read invoices
            or purchase orders back out of Restaurant 365 (see docs/RECEIPT_SCAN_MODULE.md).
          </p>

          {r365Loading ? (
            <span className="spinner-border spinner-border-sm text-secondary" />
          ) : (
            <>
              {r365Error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{r365Error}</div>}
              {r365Saved && <div className="alert alert-success py-2" style={{ fontSize: 13 }}>Saved.</div>}

              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label small mb-1">Subdomain</label>
                  <div className="input-group input-group-sm">
                    <input
                      type="text" className="form-control"
                      placeholder="yourcompany"
                      value={r365Form.r365Subdomain}
                      onChange={e => setR365Form(f => ({ ...f, r365Subdomain: e.target.value }))}
                    />
                    <span className="input-group-text" style={{ fontSize: 11 }}>.restaurant365.com</span>
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label small mb-1">Username</label>
                  <input
                    type="text" className="form-control form-control-sm"
                    value={r365Form.r365Username}
                    onChange={e => setR365Form(f => ({ ...f, r365Username: e.target.value }))}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small mb-1">
                    Password {r365?.hasPassword && <span className="text-muted">(leave blank to keep current)</span>}
                  </label>
                  <input
                    type="password" className="form-control form-control-sm"
                    value={r365Form.r365Password}
                    onChange={e => setR365Form(f => ({ ...f, r365Password: e.target.value }))}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small mb-1">Default Location</label>
                  <input
                    type="text" className="form-control form-control-sm"
                    placeholder="exact Location name/code as it appears in R365"
                    value={r365Form.r365DefaultLocation}
                    onChange={e => setR365Form(f => ({ ...f, r365DefaultLocation: e.target.value }))}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small mb-1">Default GL Account</label>
                  <input
                    type="text" className="form-control form-control-sm"
                    placeholder="fallback account when an ingredient has none of its own"
                    value={r365Form.r365DefaultGLAccount}
                    onChange={e => setR365Form(f => ({ ...f, r365DefaultGLAccount: e.target.value }))}
                  />
                </div>
              </div>

              <div className="d-flex gap-2 align-items-center flex-wrap">
                <button className="btn btn-sm btn-outline-primary" onClick={handleR365Save} disabled={r365Saving}>
                  {r365Saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : 'Save'}
                </button>
                {r365?.connected && (
                  <button className="btn btn-sm btn-outline-secondary" onClick={handleR365Test} disabled={r365Testing}>
                    {r365Testing ? <><span className="spinner-border spinner-border-sm me-1" />Testing…</> : 'Test Connection'}
                  </button>
                )}
              </div>

              {r365TestResult && (
                <div className={`alert py-2 px-3 mt-2 mb-0 ${r365TestResult.ok ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: 12 }}>
                  {r365TestResult.ok ? <>✅ {r365TestResult.message}</> : <>❌ {r365TestResult.message}</>}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Shopify integration — manual webhook setup for direct-to-consumer orders */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex align-items-center gap-2 mb-1">
            <h6 className="card-title mb-0">Shopify</h6>
            {!shopifyLoading && (
              shopify?.shopifyShopDomain
                ? <span className="badge bg-success" style={{ fontSize: 10 }}>Configured</span>
                : <span className="badge bg-secondary" style={{ fontSize: 10 }}>Not configured</span>
            )}
          </div>
          <p className="text-muted mb-3" style={{ fontSize: 12 }}>
            Direct-to-consumer Shopify orders are created here automatically, pre-confirmed and
            ready for the factory, the moment they're paid. No Shopify app or OAuth needed — paste
            the details below into your Shopify Admin.
          </p>

          {shopifyLoading ? (
            <span className="spinner-border spinner-border-sm text-secondary" />
          ) : (
            <>
              {shopifyError && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{shopifyError}</div>}
              {shopifySaved && <div className="alert alert-success py-2" style={{ fontSize: 13 }}>Saved.</div>}

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label small mb-1">Shop domain</label>
                  <input
                    type="text" className="form-control form-control-sm"
                    placeholder="yourstore.myshopify.com"
                    value={shopifyForm.shopifyShopDomain}
                    onChange={e => setShopifyForm(f => ({ ...f, shopifyShopDomain: e.target.value }))}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small mb-1">Default driver payout ($)</label>
                  <input
                    type="number" min="0" step="0.01" className="form-control form-control-sm"
                    placeholder="e.g. 15.00"
                    value={shopifyForm.defaultDriverPayoutUsd}
                    onChange={e => setShopifyForm(f => ({ ...f, defaultDriverPayoutUsd: e.target.value }))}
                  />
                </div>
              </div>

              {shopify?.webhookUrl && (
                <div className="mb-2">
                  <label className="form-label small mb-1">Webhook URL</label>
                  <div className="input-group input-group-sm">
                    <input type="text" className="form-control" readOnly value={shopify.webhookUrl} style={{ fontFamily: 'monospace', fontSize: 11 }} />
                    <button className="btn btn-outline-secondary" onClick={() => copyShopify('url', shopify.webhookUrl)}>
                      {shopifyCopied === 'url' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              {shopify && (
                <div className="mb-3">
                  <label className="form-label small mb-1">Webhook secret</label>
                  <div className="input-group input-group-sm">
                    <input
                      type="text" className="form-control" style={{ fontFamily: 'monospace', fontSize: 11 }}
                      value={shopify.webhookSecret || ''}
                      onChange={e => setShopify(s => ({ ...s, webhookSecret: e.target.value }))}
                    />
                    <button className="btn btn-outline-secondary" onClick={() => copyShopify('secret', shopify.webhookSecret)}>
                      {shopifyCopied === 'secret' ? 'Copied!' : 'Copy'}
                    </button>
                    <button className="btn btn-outline-secondary" onClick={() => handleShopifySave(true)} disabled={shopifySaving}>
                      Generate new
                    </button>
                  </div>
                  <div className="form-text" style={{ fontSize: 11 }}>
                    Editable — if Shopify assigns its own signing secret for this webhook (rather
                    than letting you set one), paste Shopify's value here instead and hit Save.
                  </div>
                </div>
              )}

              <div className="d-flex gap-2 align-items-center flex-wrap">
                <button className="btn btn-sm btn-outline-primary" onClick={() => handleShopifySave(false)} disabled={shopifySaving}>
                  {shopifySaving ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : 'Save'}
                </button>
                {shopify?.webhookSecret && (
                  <button className="btn btn-sm btn-outline-secondary" onClick={handleShopifyTestOrder} disabled={shopifyTesting}>
                    {shopifyTesting ? <><span className="spinner-border spinner-border-sm me-1" />Sending…</> : 'Send test order'}
                  </button>
                )}
              </div>

              {shopifyTestResult && (
                <div className={`alert py-2 px-3 mt-2 mb-0 ${shopifyTestResult.ok ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: 12, fontFamily: 'monospace' }}>
                  {shopifyTestResult.ok ? (
                    <>✅ Delivered — order created in quickLOG (status {shopifyTestResult.status}). Check the Factory Orders queue.</>
                  ) : (
                    <>❌ {shopifyTestResult.error || `Server responded ${shopifyTestResult.status}`}{shopifyTestResult.body ? ` — ${JSON.stringify(shopifyTestResult.body)}` : ''}</>
                  )}
                </div>
              )}

              <div className="mt-3 pt-3 border-top" style={{ fontSize: 12 }} >
                <strong>Setup, in Shopify Admin:</strong>
                <ol className="text-muted mb-0 ps-3 mt-1">
                  <li>Settings → Notifications → scroll to <strong>Webhooks</strong> → Create webhook.</li>
                  <li>Event: <strong>Order payment</strong>. Format: <strong>JSON</strong>.</li>
                  <li>Paste the Webhook URL above into the URL field.</li>
                  <li>Check what Shopify shows for signing this webhook — if it lets you choose a
                    secret, use the one generated above; if Shopify assigns its own instead, copy
                    that value into the field above and Save so both sides match.</li>
                </ol>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        show={confirmQboDisconnect}
        title="Disconnect QuickBooks Online"
        message="Disconnect from QuickBooks Online? Token data will be removed. Your existing QuickBooks invoices are not affected. You can reconnect at any time."
        confirmLabel="Disconnect"
        confirmVariant="danger"
        onConfirm={handleQboDisconnect}
        onCancel={() => setConfirmQboDisconnect(false)}
      />
      <ConfirmModal
        show={confirmXeroDisconnect}
        title="Disconnect Xero"
        message="Disconnect from Xero? Token data will be removed. Your existing Xero invoices are not affected. You can reconnect at any time."
        confirmLabel="Disconnect"
        confirmVariant="danger"
        onConfirm={handleXeroDisconnect}
        onCancel={() => setConfirmXeroDisconnect(false)}
      />

      {/* ── AI Usage ── */}
      {aiUsage && (
        <>
          <h6 className="text-muted fw-semibold mt-4 mb-2" style={{ fontSize: 12, letterSpacing: 1 }}>AI USAGE</h6>
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title mb-3">AI Token Usage</h6>
              <p className="text-muted mb-3" style={{ fontSize: 12 }}>
                Tracks AI feature usage (checklist import, policy generation, etc.) across all modules.
                Budgets are set by the platform administrator.
              </p>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
                    <span>Today's spend</span>
                    <span>
                      <strong>${aiUsage.dailySpendUsd.toFixed(3)}</strong>
                      {aiUsage.aiDailyBudgetUsd && <span className="text-muted"> / ${aiUsage.aiDailyBudgetUsd} limit</span>}
                    </span>
                  </div>
                  {aiUsage.aiDailyBudgetUsd ? (() => {
                    const pct = Math.min(100, (aiUsage.dailySpendUsd / aiUsage.aiDailyBudgetUsd) * 100);
                    return (
                      <div className="progress" style={{ height: 8 }}>
                        <div
                          className={`progress-bar ${pct >= 100 ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-success'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    );
                  })() : (
                    <div className="progress" style={{ height: 8 }}>
                      <div className="progress-bar bg-secondary" style={{ width: '100%', opacity: 0.15 }} />
                    </div>
                  )}
                  {aiUsage.aiDailyBudgetUsd && aiUsage.dailySpendUsd >= aiUsage.aiDailyBudgetUsd && (
                    <div className="badge bg-danger mt-1" style={{ fontSize: 10 }}>Daily limit reached — AI features paused</div>
                  )}
                </div>
                <div className="col-md-6">
                  <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
                    <span>This month</span>
                    <span>
                      <strong>${aiUsage.monthlySpendUsd.toFixed(2)}</strong>
                      {aiUsage.aiMonthlyBudgetUsd && <span className="text-muted"> / ${aiUsage.aiMonthlyBudgetUsd} limit</span>}
                    </span>
                  </div>
                  {aiUsage.aiMonthlyBudgetUsd ? (() => {
                    const pct = Math.min(100, (aiUsage.monthlySpendUsd / aiUsage.aiMonthlyBudgetUsd) * 100);
                    return (
                      <div className="progress" style={{ height: 8 }}>
                        <div
                          className={`progress-bar ${pct >= 100 ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-success'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    );
                  })() : (
                    <div className="progress" style={{ height: 8 }}>
                      <div className="progress-bar bg-secondary" style={{ width: '100%', opacity: 0.15 }} />
                    </div>
                  )}
                  {aiUsage.aiMonthlyBudgetUsd && aiUsage.monthlySpendUsd >= aiUsage.aiMonthlyBudgetUsd && (
                    <div className="badge bg-danger mt-1" style={{ fontSize: 10 }}>Monthly limit reached — AI features paused</div>
                  )}
                </div>
                {!aiUsage.aiDailyBudgetUsd && !aiUsage.aiMonthlyBudgetUsd && (
                  <div className="col-12">
                    <p className="text-muted mb-0" style={{ fontSize: 12 }}>No spending limits configured. Contact your platform administrator to set a budget.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {aiUsage.maxConcurrentRequests != null && (
            <div className="card mb-3">
              <div className="card-body">
                <h6 className="card-title mb-2">Concurrent Request Limit</h6>
                <p className="text-muted mb-2" style={{ fontSize: 12 }}>
                  The maximum number of API requests your team can have in flight at the same
                  moment. Set by the platform administrator based on your plan and database
                  capacity — if your team sees "system busy" messages often, let us know and we
                  can review this.
                </p>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {aiUsage.maxConcurrentRequests} <span className="text-muted" style={{ fontSize: 13, fontWeight: 400 }}>concurrent requests</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Subscription & Billing ── */}
      {subscriptionInfo && getRoleFromToken() !== 'superadmin' && (
        <>
          <h6 className="text-muted fw-semibold mt-4 mb-2" style={{ fontSize: 12, letterSpacing: 1 }}>SUBSCRIPTION & BILLING</h6>
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title mb-3">Subscription</h6>
              <div className="d-flex align-items-center gap-2 mb-2">
                {subscriptionInfo.billingExempt ? (
                  <span className="badge bg-success">Billing Exempt</span>
                ) : subscriptionInfo.status === 'active' ? (
                  <span className="badge bg-success">Active</span>
                ) : subscriptionInfo.status === 'trialing' ? (
                  <span className="badge bg-warning text-dark">Trial</span>
                ) : subscriptionInfo.status === 'incomplete' ? (
                  <span className="badge bg-warning text-dark">Pending Payment</span>
                ) : subscriptionInfo.status === 'past_due' ? (
                  <span className="badge bg-danger">Past Due</span>
                ) : subscriptionInfo.status === 'canceled' ? (
                  <span className="badge bg-secondary">Canceled</span>
                ) : subscriptionInfo.status === 'trial_expired' ? (
                  <span className="badge bg-danger">Trial Expired</span>
                ) : (
                  <span className="badge bg-secondary">{subscriptionInfo.status}</span>
                )}
                {subscriptionInfo.planTier && (
                  <span className="text-muted small text-capitalize">{subscriptionInfo.planTier} plan</span>
                )}
              </div>

              {subscriptionInfo.status === 'incomplete' && (
                <div className="alert alert-warning py-2 mb-2" style={{ fontSize: 13 }}>
                  Payment is required to activate your subscription. Click below to add a payment method.
                </div>
              )}
              {subscriptionInfo.status === 'trialing' && subscriptionInfo.trialEndsAt && (
                <p className="text-muted small mb-2">
                  Trial ends {new Date(subscriptionInfo.trialEndsAt).toLocaleDateString()}
                </p>
              )}
              {subscriptionInfo.status === 'active' && subscriptionInfo.currentPeriodEnd && (
                <p className="text-muted small mb-2">
                  Next billing date: {new Date(subscriptionInfo.currentPeriodEnd).toLocaleDateString()}
                </p>
              )}

              {portalError && <div className="alert alert-danger py-1 small mb-2">{portalError}</div>}

              {!subscriptionInfo.billingExempt && (
                <>
                  <button
                    className={`btn btn-sm ${subscriptionInfo.status === 'incomplete' ? 'btn-warning' : 'btn-outline-primary'}`}
                    onClick={handleOpenBillingPortal}
                    disabled={portalLoading}
                  >
                    {portalLoading
                      ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Opening…</>
                      : subscriptionInfo.status === 'incomplete' ? 'Add Payment Method' : 'Manage Billing & Payment'}
                  </button>
                  <p className="text-muted small mt-2 mb-0">
                    Opens the Stripe billing portal — update payment method, download invoices, or manage your subscription.
                  </p>
                </>
              )}
            </div>
          </div>
        </>
      )}

    </div>
  );
}

export default CompanySettingsPage;
